import { useTranslation } from "react-i18next"

import { Card, CardContent } from "@/components/ui/card"
import { cn } from "@/lib/utils"

/**
 * OKS-38 — Single KPI tile on the brand dashboard.
 *
 * Renders a label (small muted text) + numeric value (large semibold).
 * Three render modes via `kind`:
 *
 *   - `"integer"` (default): renders the value with locale-aware
 *     thousands separator (`Intl.NumberFormat(i18n.language)`); 0 is
 *     rendered explicitly as "0" (UX Spec §3 edge).
 *   - `"percent"`: multiplies a `[0, 1]` ratio by 100 + rounds to whole
 *     number, suffixed with "%". `null` value renders the em-dash with
 *     `aria-label="not enough data"` (UX Spec §6 a11y lock).
 *
 * The tile is a `<div role="group">` (NOT a button) — the dashboard is
 * read-only in V1; tiles do NOT navigate.
 */

export type DashboardKpiTileKind = "integer" | "percent"

export interface DashboardKpiTileProps {
  /** i18n key resolving to the tile label (e.g. `brand_home.kpis.active_crew.label`). */
  labelKey: string
  /** i18n key resolving to the aria-label template (interpolates `{value}`). */
  ariaLabelKey: string
  /**
   * i18n key for the empty/null aria-label (`completion_rate` only —
   * other tiles never receive `null`). When `value === null` and this
   * key is provided, it's used in place of `ariaLabelKey`.
   */
  emptyAriaKey?: string
  /**
   * Numeric value. `null` is only legitimate for `kind: "percent"`
   * (Completion Rate when no collabs ever) — renders the em-dash.
   */
  value: number | null
  kind?: DashboardKpiTileKind
  /** Stable test hook for system + Vitest specs. */
  testId?: string
  className?: string
}

const EM_DASH = "—"

export function DashboardKpiTile({
  labelKey,
  ariaLabelKey,
  emptyAriaKey,
  value,
  kind = "integer",
  testId,
  className,
}: DashboardKpiTileProps) {
  const { t, i18n } = useTranslation()

  const isNull = value === null
  const displayValue = formatValue(value, kind, i18n.language)
  const ariaLabel =
    isNull && emptyAriaKey
      ? t(emptyAriaKey)
      : t(ariaLabelKey, { value: displayValue })

  return (
    <Card
      data-testid={testId}
      role="group"
      aria-label={ariaLabel}
      className={cn("rounded-xl border p-5", className)}
    >
      <CardContent className="flex flex-col gap-1 p-0">
        <span className="text-muted-foreground text-sm font-medium">
          {t(labelKey)}
        </span>
        <span
          className="text-foreground text-3xl font-semibold tracking-tight"
          // The visual em-dash is decorative — the real announcement
          // comes from the role=group `aria-label`. Belt-and-braces in
          // case a SR reads the inner span for some reason.
          aria-hidden={isNull ? true : undefined}
        >
          {displayValue}
        </span>
      </CardContent>
    </Card>
  )
}

function formatValue(
  value: number | null,
  kind: DashboardKpiTileKind,
  locale: string,
): string {
  if (value === null) return EM_DASH

  if (kind === "percent") {
    // [0, 1] ratio → whole-number percent. Round to nearest int per
    // UX Spec §2 ("87%" — whole number, no decimals in V1).
    const pct = Math.round(value * 100)
    return `${pct}%`
  }

  // Integer with locale-aware thousands separator. `Intl.NumberFormat`
  // gracefully handles unknown locales (falls back to `en`).
  return new Intl.NumberFormat(locale).format(value)
}
